import cx from 'classnames'
import NextImage from 'next/image'
import { type Dispatch, type SetStateAction, useCallback, useMemo } from 'react'

import { type SanityFrameBuilderTextureImage } from '../../data/sanity/queries/types/texture'
import { getBasicImageLoader, getSanityImageUrl } from '../../lib/image'

interface ThumbnailProps {
  index: number
  image: SanityFrameBuilderTextureImage
  setSelectedIndex: Dispatch<SetStateAction<number>>
  setIsModalOpen: Dispatch<SetStateAction<boolean>>
  className?: string
}

interface ThumbnailsProps {
  images: SanityFrameBuilderTextureImage[]
  setSelectedIndex: Dispatch<SetStateAction<number>>
  setIsModalOpen: Dispatch<SetStateAction<boolean>>
  className?: string
}

const Thumbnail = ({
  index,
  image,
  setSelectedIndex,
  setIsModalOpen,
  className,
}: ThumbnailProps) => {
  const imageUrl = useMemo(
    () =>
      getSanityImageUrl(image.image, {
        width: 64,
        height: 64,
      }),
    [image.image],
  )

  const handleClick = useCallback(() => {
    setSelectedIndex(index)
    setIsModalOpen(true)
  }, [index, setSelectedIndex, setIsModalOpen])

  if (!imageUrl) {
    return
  }

  return (
    <div
      className={cx(
        'shrink-0 border border-gray-700 cursor-pointer',

        className,
      )}
    >
      <NextImage
        src={imageUrl}
        width={64}
        height={64}
        alt={image.alternativeText}
        loader={getBasicImageLoader}
        unoptimized
        onClick={handleClick}
        className="block object-cover w-[32px] h-[32px] xs:w-[48px] xs:h-[48px] lg:w-[64px] lg:h-[64px]"
      />
    </div>
  )
}

const Thumbnails = ({
  images,
  setSelectedIndex,
  setIsModalOpen,
  className,
}: ThumbnailsProps) => {
  return (
    <div
      className={cx(
        'flex justify-center sm:flex-col items-center gap-1.5 py-2.5',
        className,
      )}
    >
      {images.map((image, index) => (
        <Thumbnail
          key={index}
          index={index}
          image={image}
          setSelectedIndex={setSelectedIndex}
          setIsModalOpen={setIsModalOpen}
        />
      ))}
    </div>
  )
}

export default Thumbnails
